import { GetCultureName } from '../stores/SettingsStore'
import type { IAOEConfig } from '../types/AoeTypes'

declare const AOEConfig: IAOEConfig

// Cache for the default currency based on culture
let cachedCurrency: string | null = null
let initializationPromise: Promise<void> | null = null

/**
 * Initialize the currency formatter with the culture from settings
 * This should be called early in the app lifecycle
 */
export async function InitializeCurrencyFormatter(): Promise<void> {
  if (initializationPromise) {
    return initializationPromise
  }
  
  initializationPromise = (async () => {
    try {
      const cultureName = await GetCultureName()
      cachedCurrency = GetCurrencyFromCulture(cultureName)
      console.log('Currency formatter initialized:', { cultureName, cachedCurrency })
    } catch (error) {
      console.error('Failed to initialize currency formatter:', error)
      cachedCurrency = 'EUR'
    }
  })()
  
  return initializationPromise
}

/**
 * Lazy initialization - call this if currency hasn't been initialized yet
 */
async function ensureInitialized(): Promise<void> {
  if (cachedCurrency === null) {
    await InitializeCurrencyFormatter()
  }
}

/**
 * Get currency code from culture name
 * @param cultureName - Culture name like 'de-DE', 'en-US', etc.
 * @returns Currency code like 'EUR', 'USD', etc.
 */
export function GetCurrencyFromCulture(cultureName: string | null): string {
  if (!cultureName) return 'USD'

  const cultureMap: Record<string, string> = {
    'de-DE': 'EUR',
    'de-AT': 'EUR',
    'fr-FR': 'EUR',
    'en-US': 'USD',
    'en-GB': 'GBP',
    'ja-JP': 'JPY',
  }

  return cultureMap[cultureName] || 'EUR'
}

export function FormatCurrency(amount: number, passedCurrency: string | null) {
  if (amount === undefined || amount === null) {
    return null
  }
  if (isNaN(Number(amount))) {
    return String(amount)
  }

  // Determine the currency to use
  // If no currency is passed, use the cached currency from culture
  // Fall back to AOEConfig.I18nLocale if cache not ready yet
  let currency = passedCurrency || cachedCurrency
  
  if (!currency) {
    // Try to get currency from AOEConfig as immediate fallback
    try {
      const locale = AOEConfig?.I18nLocale || 'en-US'
      currency = GetCurrencyFromCulture(locale)
    } catch {
      currency = 'EUR'
    }
  }
  
  // Debug logging (remove after testing)
  if (!passedCurrency && amount === 50) {
    console.log('FormatCurrency called:', { amount, passedCurrency, cachedCurrency, currency, AOEConfigLocale: AOEConfig?.I18nLocale })
  }

  // For EUR, use a European locale to ensure symbol is at the end
  // For other currencies, use appropriate locale
  let locale = navigator.language || 'en-US'

  if (currency === 'EUR') {
    // Force European locale for EUR to get proper formatting (symbol at end)
    locale = 'de-DE' // German locale formats EUR as "50,00 €"
  } else if (currency === 'USD') {
    locale = 'en-US'
  } else if (currency === 'GBP') {
    locale = 'en-GB'
  } else if (currency === 'JPY') {
    locale = 'ja-JP'
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  })

  return formatter.format(amount)
}

/**
 * Format a number with European-style thousand separators (dots)
 * No currency symbol, just the formatted number
 * @param value - The number to format
 * @returns Formatted string like "1.234" or "1.234,56"
 */
export function FormatNumber(value: number, decimals: number = 0): string | null {
  if (value === undefined || value === null) {
    return null
  }
  if (isNaN(Number(value))) {
    return String(value)
  }

  const formatter = new Intl.NumberFormat('de-DE', {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })

  return formatter.format(value)
}
